
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { MediaItemData, MediaType, sampleMediaGalleryData } from "../types/mediaTypes";

// Context type
interface MediaContextProps {
  activeFilter: MediaType;
  setActiveFilter: React.Dispatch<React.SetStateAction<MediaType>>;
  selectedMedia: MediaItemData | null;
  setSelectedMedia: React.Dispatch<React.SetStateAction<MediaItemData | null>>;
  filteredMedia: MediaItemData[];
  mediaItems: MediaItemData[];
  setMediaItems: React.Dispatch<React.SetStateAction<MediaItemData[]>>;
  updateMediaItem: (updatedItem: MediaItemData) => void;
  isEditMode: boolean;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  loading: boolean;
  refreshMediaItems: () => Promise<void>;
  addNewMediaItem: (type: "infographic" | "photo" | "video") => MediaItemData;
  deleteMediaItem: (id: string) => Promise<void>;
}

// Create context
const MediaContext = createContext<MediaContextProps | undefined>(undefined);

// Provider component
export function MediaProvider({ children }: { children: ReactNode }) {
  const [activeFilter, setActiveFilter] = useState<MediaType>("all");
  const [selectedMedia, setSelectedMedia] = useState<MediaItemData | null>(null);
  const [mediaItems, setMediaItems] = useState<MediaItemData[]>([]);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  
  console.log("MediaProvider initialized");
  
  // Create a new media item
  const addNewMediaItem = (type: "infographic" | "photo" | "video"): MediaItemData => {
    const newItem: MediaItemData = {
      id: `new-${Date.now()}`, // Generate a temporary unique ID
      title: {
        en: "",
        ar: ""
      },
      type,
      image: "",
      videoUrl: type === "video" ? "" : undefined,
      sdgId: 1
    };
    
    // Add the new item to the state
    setMediaItems(prev => [newItem, ...prev]);
    
    return newItem;
  };

  // Delete a media item
  const deleteMediaItem = async (id: string) => {
    try {
      console.log("Deleting media item with ID:", id);
      
      // Update state by removing the item
      setMediaItems(prev => prev.filter(item => item.id !== id));
      
      // If the deleted item was selected, clear selection
      if (selectedMedia && selectedMedia.id === id) {
        setSelectedMedia(null);
      }
    } catch (error) {
      console.error("Error deleting media item:", error);
      throw error;
    }
  };

  // Filter media based on active filter
  const filteredMedia = activeFilter === "all" 
    ? mediaItems 
    : mediaItems.filter(item => item.type === activeFilter);

  // Update a media item
  const updateMediaItem = (updatedItem: MediaItemData) => {
    setMediaItems(prev => {
      const existingItemIndex = prev.findIndex(item => item.id === updatedItem.id);
      
      if (existingItemIndex === -1) {
        // This is a new item, add it to the array
        return [updatedItem, ...prev];
      } else {
        // This is an existing item being updated
        const newItems = [...prev];
        newItems[existingItemIndex] = updatedItem;
        return newItems;
      }
    });
    
    // If the updated item is currently selected, update the selection too
    if (selectedMedia && selectedMedia.id === updatedItem.id) {
      setSelectedMedia(updatedItem);
    }
  };

  // Refresh media items from sample data
  const refreshMediaItems = async () => {
    try {
      setLoading(true);
      console.log("Loading sample media items...");
      
      // Use timeout to simulate loading
      setTimeout(() => {
        setMediaItems(sampleMediaGalleryData);
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error("Error loading sample media items:", error);
      setMediaItems(sampleMediaGalleryData);
      setLoading(false);
    }
  };
  
  // Load media items on initial render
  useEffect(() => {
    console.log("MediaProvider mounted, loading sample media items");
    refreshMediaItems();
  }, []);

  return (
    <MediaContext.Provider value={{ 
      activeFilter, 
      setActiveFilter, 
      selectedMedia, 
      setSelectedMedia,
      filteredMedia,
      mediaItems,
      setMediaItems,
      updateMediaItem,
      isEditMode,
      setIsEditMode,
      loading,
      refreshMediaItems,
      addNewMediaItem,
      deleteMediaItem
    }}>
      {children}
    </MediaContext.Provider>
  );
}

// Custom hook to use the media context
export function useMedia() {
  const context = useContext(MediaContext);
  
  if (context === undefined) {
    throw new Error("useMedia must be used within a MediaProvider");
  }
  
  return context;
}
